/**
 * executionIntelligence.ts — MER Algo Footprints v8.8
 *
 * Execution Intelligence Layer.
 *
 * Transforms structured intelligence outputs into deterministic trade plans.
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — same inputs → same outputs, no Date.now() in semantic output
 * 2. No execution — this layer plans, never orders
 * 3. Deterministic — all thresholds centralized in EXECUTION_THRESHOLDS
 * 4. Explainable — every field carries a reason string
 * 5. Replay-safe — serializable inputs/outputs
 *
 * ── Input sources (all post-engine, read-only) ────────────────────────────────
 *   SymbolContext  — gamma, liquidity zones, SMC, regime, traps, setups, price
 *   ValidatedAIResponse — thesis bias, conviction, entry/target/invalidation logic
 *
 * ── Output hierarchy ─────────────────────────────────────────────────────────
 *   SetupCandidate[]     — detected setups with type + evidence
 *   TradePlan            — full plan: entry zone, risk level, targets, scenarios
 *   ExecutionPlanOutput  — top plan + all candidates + quality scores
 */

import type { SymbolContext }      from '../market/services/buildSymbolContext';
import type { ValidatedAIResponse } from '../ai/types/responseSchema';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const EXECUTION_THRESHOLDS = {
  // Conviction floor — below this, no plan is generated
  MIN_CONVICTION:              40,
  // Actionability floor
  MIN_ACTIONABILITY:           35,

  // Entry zone width: ± this % of price around entry
  ENTRY_ZONE_WIDTH_PCT:        0.003,    // 0.3%

  // Stop distance defaults by setup type
  STOP_PCT_BREAKOUT:           0.008,    // 0.8% below/above entry
  STOP_PCT_REVERSAL:           0.010,    // 1.0%
  STOP_PCT_CONTINUATION:       0.006,    // 0.6%
  STOP_PCT_TRAP:               0.012,    // 1.2% — wider for trap reversals

  // Minimum R:R to include a target
  MIN_RR_TARGET:               1.5,

  // Plan quality score components (weights must sum to 1.0)
  WEIGHT_STRUCTURE:            0.30,
  WEIGHT_CONFLUENCE:           0.25,
  WEIGHT_VOLATILITY_FIT:       0.20,
  WEIGHT_EDGE_ALIGNMENT:       0.25,

  // Minimum plan quality to surface as actionable
  MIN_QUALITY_ACTIONABLE:      0.55,

  // Regime confidence floor for including regime as a confluence factor
  REGIME_CONF_MIN:             60,

  // Trap strength thresholds
  TRAP_STRONG_THRESHOLD:       'strong',
} as const;

const T = EXECUTION_THRESHOLDS;

// ── Types ─────────────────────────────────────────────────────────────────────

export type SetupType =
  | 'continuation'
  | 'reversal'
  | 'trap_reversal'
  | 'breakout'
  | 'breakout_fade';

export type EntryType = 'limit' | 'confirmation' | 'breakout_trigger';
export type Direction = 'long' | 'short';
export type PlanQualityTier = 'high' | 'moderate' | 'low' | 'insufficient';

export interface SetupCandidate {
  type:        SetupType;
  direction:   Direction;
  /** 0–100 evidence score — how strongly the inputs support this setup */
  evidence:    number;
  /** Named rules that fired */
  rules:       string[];
  /** Human-readable rationale */
  rationale:   string;
}

export interface EntryZone {
  type:     EntryType;
  /** Ideal entry price */
  idealEntry:   number;
  /** Zone bottom (for longs: below entry) */
  zoneBottom:   number;
  /** Zone top (for shorts: above entry) */
  zoneTop:      number;
  /** What must happen before entering */
  condition:    string;
  reason:       string;
}

export interface RiskLevel {
  /** Hard stop-loss price */
  stopPrice:     number;
  /** Distance from entry in % */
  stopPct:       number;
  /** Dollar distance per share */
  stopDistance:  number;
  /** The structural or engine signal that defines this level */
  structuralBasis: string;
  /** What event makes the thesis invalid */
  invalidationEvent: string;
}

export interface PriceTarget {
  price:       number;
  /** R multiple vs the stop */
  rMultiple:   number;
  basis:       string;    // 'liquidity zone' | 'gamma wall' | 'structural level' | 'thesis'
  reason:      string;
}

export interface Scenario {
  /** Condition that triggers this path */
  trigger:     string;
  /** What to expect if triggered */
  expectation: string;
  /** Suggested operator response */
  response:    string;
  /** Probability weight — must sum to ~1 across scenarios */
  weight:      number;
}

export interface PlanQualityScore {
  overall:         number;    // 0–1
  tier:            PlanQualityTier;
  structureScore:  number;
  confluenceScore: number;
  volatilityFit:   number;
  edgeAlignment:   number;
  /** Factors that boosted or capped the score */
  notes:           string[];
}

export interface TradePlan {
  symbol:      string;
  setupType:   SetupType;
  direction:   Direction;
  entryZone:   EntryZone;
  risk:        RiskLevel;
  targets:     PriceTarget[];
  scenarios:   Scenario[];
  quality:     PlanQualityScore;
  /** One-line plan summary for display */
  summary:     string;
  /** Ordered reasoning trace */
  reasoning:   string[];
}

export interface ExecutionPlanOutput {
  symbol:          string;
  candidates:      SetupCandidate[];
  /** Best plan (from highest-evidence candidate, null if below thresholds) */
  primaryPlan:     TradePlan | null;
  /** All plans generated, sorted by quality */
  allPlans:        TradePlan[];
  /** Why no plan was generated (when primaryPlan is null) */
  noplanReason:    string | null;
}

// ── Step 1: Setup detection ───────────────────────────────────────────────────

function _detectSetups(
  ctx:    SymbolContext,
  thesis: ValidatedAIResponse | null,
): SetupCandidate[] {
  const candidates: SetupCandidate[] = [];
  const price    = ctx.curPrice;
  const trap     = ctx.trapInterpretation;
  const regime   = ctx.regime;
  const smc      = ctx.smc;
  const gamma    = ctx.gamma;

  // ── RULE_TRAP_REVERSAL ────────────────────────────────────────────────────
  if (trap && trap.trapClassification !== 'none' && trap.trapStrength === T.TRAP_STRONG_THRESHOLD) {
    const dir: Direction = trap.trapSide?.includes('bull') ? 'short' : 'long';
    const rules = ['RULE_TRAP_REVERSAL', `trap=${trap.trapClassification}`, `strength=${trap.trapStrength}`];
    const evidence = 70 + (trap.confidence ?? 0) * 0.3;
    candidates.push({
      type: 'trap_reversal', direction: dir,
      evidence: Math.min(100, Math.round(evidence)),
      rules,
      rationale: `${trap.trapClassification} trap with ${trap.trapStrength} strength — fade the trap direction`,
    });
  }

  // ── RULE_CONTINUATION ────────────────────────────────────────────────────
  if (thesis && thesis.overallBias !== 'neutral' && thesis.conviction >= T.MIN_CONVICTION) {
    const dir: Direction = thesis.overallBias === 'bullish' ? 'long' : 'short';
    const smcAligned = smc?.bias === (dir === 'long' ? 'bullish' : 'bearish');
    const regimeAligned = regime?.dominant === 'trend' || regime?.dominant === 'breakout';
    const evidence = thesis.conviction * 0.6 + (smcAligned ? 15 : 0) + (regimeAligned ? 15 : 0);
    const rules = ['RULE_CONTINUATION', `bias=${thesis.overallBias}`, `conv=${thesis.conviction}%`];
    if (smcAligned) rules.push('SMC_ALIGNED');
    if (regimeAligned) rules.push(`REGIME=${regime?.dominant}`);
    candidates.push({
      type: 'continuation', direction: dir,
      evidence: Math.min(100, Math.round(evidence)),
      rules,
      rationale: `${dir} continuation — thesis conviction ${thesis.conviction}%${smcAligned ? ', SMC aligned' : ''}`,
    });
  }

  // ── RULE_REVERSAL ─────────────────────────────────────────────────────────
  if (regime?.dominant === 'meanReversion' && (regime.confidence ?? 0) >= T.REGIME_CONF_MIN) {
    const dir: Direction = smc?.bias === 'bullish' ? 'long' : 'short';
    const flipDist = gamma?.gammaFlip != null
      ? Math.abs(price - gamma.gammaFlip) / price : null;
    const nearFlip = flipDist != null && flipDist < 0.015;
    const rules = ['RULE_REVERSAL', `regime=meanReversion(${regime.confidence}%)`];
    if (nearFlip) rules.push(`NEAR_GAMMA_FLIP(${(flipDist! * 100).toFixed(2)}%)`);
    candidates.push({
      type: 'reversal', direction: dir,
      evidence: Math.min(100, Math.round(60 + (regime.confidence ?? 0) * 0.3 + (nearFlip ? 10 : 0))),
      rules,
      rationale: `Mean-reversion regime at ${(regime.confidence ?? 0)}% confidence${nearFlip ? ', price near gamma flip' : ''}`,
    });
  }

  // ── RULE_BREAKOUT ─────────────────────────────────────────────────────────
  if (regime?.dominant === 'breakout' && (regime.confidence ?? 0) >= T.REGIME_CONF_MIN) {
    const dir: Direction = thesis?.overallBias === 'bearish' ? 'short' : 'long';
    const wallAbove = gamma?.callWall;
    const wallBelow = gamma?.putWall;
    const nearWall  = dir === 'long' && wallAbove
      ? Math.abs(price - wallAbove) / price < 0.02
      : dir === 'short' && wallBelow
      ? Math.abs(price - wallBelow) / price < 0.02
      : false;
    const rules = ['RULE_BREAKOUT', `regime=breakout(${regime.confidence}%)`];
    if (nearWall) rules.push(`NEAR_GAMMA_WALL`);
    candidates.push({
      type: 'breakout', direction: dir,
      evidence: Math.min(100, Math.round(65 + (regime.confidence ?? 0) * 0.25 + (nearWall ? 10 : 0))),
      rules,
      rationale: `Breakout regime — ${dir} bias${nearWall ? ', approaching gamma wall' : ''}`,
    });
  }

  // Stable sort: by evidence descending, then by type name
  return candidates.sort((a, b) => b.evidence - a.evidence || a.type.localeCompare(b.type));
}

// ── Step 2: Entry modeling ────────────────────────────────────────────────────

function _buildEntryZone(
  candidate: SetupCandidate,
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
): EntryZone {
  const price    = ctx.curPrice;
  const dir      = candidate.direction;
  const halfZone = price * T.ENTRY_ZONE_WIDTH_PCT;

  // Determine entry type and condition
  let entryType: EntryType = 'confirmation';
  let idealEntry = price;
  let condition  = thesis?.confirmationNeeded ?? 'Await confirmation signal';

  if (candidate.type === 'breakout' || candidate.type === 'breakout_fade') {
    entryType  = 'breakout_trigger';
    idealEntry = dir === 'long'
      ? (ctx.gamma?.callWall ?? price * 1.005)
      : (ctx.gamma?.putWall  ?? price * 0.995);
    condition = `${dir === 'long' ? 'Break and hold above' : 'Break and hold below'} ${idealEntry.toFixed(2)}`;
  } else if (candidate.type === 'trap_reversal') {
    entryType  = 'limit';
    idealEntry = price;  // trap reversals enter near current price
    condition  = 'Trap confirmed on next candle close';
  } else {
    entryType  = 'confirmation';
    // For continuation/reversal: use nearest liquidity zone as entry anchor
    const supportZones = ctx.zones.filter(z =>
      dir === 'long' ? z.price < price : z.price > price
    );
    if (supportZones.length > 0) {
      idealEntry = supportZones[0].price;
    }
    condition = thesis?.entryLogic ?? condition;
  }

  const zoneBottom = Math.min(idealEntry, price) - halfZone;
  const zoneTop    = Math.max(idealEntry, price) + halfZone;

  return {
    type: entryType,
    idealEntry: Math.round(idealEntry * 100) / 100,
    zoneBottom:  Math.round(zoneBottom * 100) / 100,
    zoneTop:     Math.round(zoneTop * 100) / 100,
    condition,
    reason: `${entryType} entry — ${candidate.rationale}`,
  };
}

// ── Step 3: Risk modeling ─────────────────────────────────────────────────────

function _buildRiskLevel(
  candidate: SetupCandidate,
  entry:     EntryZone,
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
): RiskLevel {
  const stopPctMap: Record<SetupType, number> = {
    continuation:    T.STOP_PCT_CONTINUATION,
    reversal:        T.STOP_PCT_REVERSAL,
    trap_reversal:   T.STOP_PCT_TRAP,
    breakout:        T.STOP_PCT_BREAKOUT,
    breakout_fade:   T.STOP_PCT_BREAKOUT,
  };
  const stopPct = stopPctMap[candidate.type];
  const dir = candidate.direction;

  // Try to anchor stop to a structural level
  let stopPrice: number;
  let structuralBasis: string;

  const flipZone  = ctx.gamma?.gammaFlip;
  const nearZones = ctx.zones.filter(z =>
    dir === 'long' ? z.price < entry.idealEntry : z.price > entry.idealEntry
  );

  if (dir === 'long' && flipZone && flipZone < entry.idealEntry) {
    // Stop just below gamma flip for longs
    stopPrice = flipZone * (1 - 0.002);
    structuralBasis = `Gamma flip zone (${flipZone.toFixed(2)})`;
  } else if (dir === 'short' && flipZone && flipZone > entry.idealEntry) {
    stopPrice = flipZone * (1 + 0.002);
    structuralBasis = `Gamma flip zone (${flipZone.toFixed(2)})`;
  } else if (nearZones.length > 0) {
    const anchor = nearZones[0].price;
    stopPrice = dir === 'long' ? anchor * 0.997 : anchor * 1.003;
    structuralBasis = `Liquidity zone (${anchor.toFixed(2)})`;
  } else {
    stopPrice = dir === 'long'
      ? entry.idealEntry * (1 - stopPct)
      : entry.idealEntry * (1 + stopPct);
    structuralBasis = `Default ${(stopPct * 100).toFixed(1)}% from entry`;
  }

  stopPrice = Math.round(stopPrice * 100) / 100;
  const stopDistance = Math.abs(entry.idealEntry - stopPrice);
  const actualStopPct = stopDistance / entry.idealEntry;

  return {
    stopPrice,
    stopPct:          Math.round(actualStopPct * 10000) / 100,
    stopDistance:     Math.round(stopDistance * 100) / 100,
    structuralBasis,
    invalidationEvent: thesis?.invalidation ?? `Price closes ${dir === 'long' ? 'below' : 'above'} ${stopPrice.toFixed(2)}`,
  };
}

// ── Step 4: Target modeling ───────────────────────────────────────────────────

function _buildTargets(
  candidate: SetupCandidate,
  entry:     EntryZone,
  risk:      RiskLevel,
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
): PriceTarget[] {
  const targets: PriceTarget[] = [];
  const dir          = candidate.direction;
  const stopDist     = risk.stopDistance;
  const idealEntry   = entry.idealEntry;

  // Target candidates: gamma walls, liquidity zones above/below price, thesis target
  const candidatePrices: { price: number; basis: string; reason: string }[] = [];

  // Gamma walls as targets
  if (dir === 'long' && ctx.gamma?.callWall) {
    candidatePrices.push({
      price: ctx.gamma.callWall,
      basis: 'gamma wall',
      reason: `Call wall resistance at ${ctx.gamma.callWall.toFixed(2)} — gamma-driven ceiling`,
    });
  }
  if (dir === 'short' && ctx.gamma?.putWall) {
    candidatePrices.push({
      price: ctx.gamma.putWall,
      basis: 'gamma wall',
      reason: `Put wall support at ${ctx.gamma.putWall.toFixed(2)} — gamma-driven floor`,
    });
  }

  // Liquidity zones above/below entry as targets
  const targetZones = ctx.zones
    .filter(z => dir === 'long' ? z.price > idealEntry : z.price < idealEntry)
    .sort((a, b) => dir === 'long' ? a.price - b.price : b.price - a.price)
    .slice(0, 3);

  for (const z of targetZones) {
    candidatePrices.push({
      price: z.price,
      basis: 'liquidity zone',
      reason: `${z.label || 'Liquidity'} zone at ${z.price.toFixed(2)}`,
    });
  }

  // Filter to minimum R:R and deduplicate
  const seen = new Set<number>();
  for (const cp of candidatePrices.sort((a, b) =>
    dir === 'long' ? a.price - b.price : b.price - a.price
  )) {
    if (seen.has(cp.price)) continue;
    const priceDist = Math.abs(cp.price - idealEntry);
    const rr = stopDist > 0 ? priceDist / stopDist : 0;
    if (rr < T.MIN_RR_TARGET) continue;
    seen.add(cp.price);
    targets.push({
      price:     Math.round(cp.price * 100) / 100,
      rMultiple: Math.round(rr * 10) / 10,
      basis:     cp.basis,
      reason:    cp.reason,
    });
  }

  // Always include a thesis-derived target if available
  if (thesis?.targetLogic && targets.length < 2) {
    const fallbackTarget = dir === 'long'
      ? idealEntry + stopDist * 2.5
      : idealEntry - stopDist * 2.5;
    if (!seen.has(fallbackTarget)) {
      targets.push({
        price:     Math.round(fallbackTarget * 100) / 100,
        rMultiple: 2.5,
        basis:     'thesis',
        reason:    thesis.targetLogic.slice(0, 100),
      });
    }
  }

  return targets.slice(0, 4);
}

// ── Step 5: Scenario modeling ─────────────────────────────────────────────────

function _buildScenarios(
  candidate: SetupCandidate,
  entry:     EntryZone,
  risk:      RiskLevel,
  targets:   PriceTarget[],
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
): Scenario[] {
  const dir  = candidate.direction;
  const t1   = targets[0];
  const t2   = targets[1];
  const trap = ctx.trapInterpretation;

  const scenarios: Scenario[] = [
    // Bull/Bear case — thesis plays out
    {
      trigger:     `Entry fills and price holds above/near ${entry.zoneBottom.toFixed(2)}`,
      expectation: t1
        ? `Move toward T1 at ${t1.price.toFixed(2)} (${t1.rMultiple}R)`
        : `Trend continuation in ${dir} direction`,
      response:    `Hold position, trail stop toward entry after T1`,
      weight:      0.50,
    },
    // Stop-out / invalidation
    {
      trigger:     `Price breaks ${risk.stopPrice.toFixed(2)} (${risk.invalidationEvent})`,
      expectation: `Thesis invalidated — momentum reversal likely`,
      response:    `Exit flat. Re-assess after next candle close.`,
      weight:      0.25,
    },
  ];

  // Extended target scenario (if 2+ targets)
  if (t2) {
    scenarios.push({
      trigger:     `T1 (${t1?.price.toFixed(2) ?? '—'}) clears with momentum`,
      expectation: `Continued move to T2 at ${t2.price.toFixed(2)} (${t2.rMultiple}R)`,
      response:    `Move stop to T1, target T2`,
      weight:      0.15,
    });
  }

  // Trap scenario (if trap intelligence available)
  if (trap && trap.trapClassification !== 'none') {
    scenarios.push({
      trigger:     `${trap.trapClassification} pattern activates (${trap.trapStrength} strength)`,
      expectation: `Sharp reversal, high velocity move against initial direction`,
      response:    `Reduce size immediately. ${dir === 'long' ? 'Watch put wall for support.' : 'Watch call wall for resistance.'}`,
      weight:      0.10,
    });
  }

  // Normalize weights
  const totalWeight = scenarios.reduce((s, sc) => s + sc.weight, 0);
  for (const sc of scenarios) {
    sc.weight = Math.round((sc.weight / totalWeight) * 100) / 100;
  }

  return scenarios;
}

// ── Step 6: Plan quality scoring ──────────────────────────────────────────────

function _scorePlan(
  candidate: SetupCandidate,
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
  targets:   PriceTarget[],
): PlanQualityScore {
  const notes: string[] = [];

  // Structure score: SMC alignment + gamma context
  let structureScore = 0.5;
  if (ctx.smc?.bias && thesis) {
    const smcAligned = (ctx.smc.bias === 'bullish' && candidate.direction === 'long') ||
                       (ctx.smc.bias === 'bearish' && candidate.direction === 'short');
    structureScore = smcAligned ? 0.85 : 0.40;
    if (smcAligned) notes.push('SMC bias aligned with plan direction');
    else notes.push('SMC bias opposes plan direction — lower structure score');
  }
  if (ctx.gamma?.gammaFlip) {
    structureScore = Math.min(1.0, structureScore + 0.10);
    notes.push('Gamma flip zone available as structural anchor');
  }

  // Confluence score: count aligned signals
  let confluenceCount = 0;
  if (thesis && thesis.conviction >= 60) confluenceCount++;
  if (ctx.regime?.dominant === (candidate.type === 'breakout' ? 'breakout' : 'trend')) confluenceCount++;
  if (ctx.trapInterpretation?.trapClassification !== 'none') confluenceCount++;
  if (ctx.zones.length >= 2) confluenceCount++;
  if (candidate.direction === 'long' ? ctx.gamma?.callWall : ctx.gamma?.putWall) confluenceCount++;
  const confluenceScore = Math.min(1.0, confluenceCount * 0.20);
  notes.push(`${confluenceCount} confluence factors`);

  // Volatility fit: setup type vs volatility context
  let volatilityFit = 0.60;
  const trapVol = (ctx.trapInterpretation?.volatilityContext as { regime?: string } | null)?.regime;
  if (candidate.type === 'breakout' && trapVol === 'high_vol') {
    volatilityFit = 0.85;
    notes.push('High-vol regime supports breakout setup');
  } else if (candidate.type === 'reversal' && trapVol === 'low_vol') {
    volatilityFit = 0.75;
    notes.push('Low-vol regime supports reversal/mean-reversion');
  } else if (trapVol === 'extreme_vol') {
    volatilityFit = 0.45;
    notes.push('Extreme volatility — wider stops required, lower vol fit');
  }

  // Edge alignment: R:R quality + target count
  const bestRR = targets.reduce((max, t) => Math.max(max, t.rMultiple), 0);
  let edgeAlignment = Math.min(1.0, bestRR / 4.0);
  if (targets.length >= 2) edgeAlignment = Math.min(1.0, edgeAlignment + 0.10);
  if (bestRR < T.MIN_RR_TARGET) {
    edgeAlignment = 0.20;
    notes.push(`Best R:R ${bestRR} is below minimum ${T.MIN_RR_TARGET}`);
  } else {
    notes.push(`Best R:R: ${bestRR}`);
  }

  const overall = Math.round((
    structureScore  * T.WEIGHT_STRUCTURE     +
    confluenceScore * T.WEIGHT_CONFLUENCE    +
    volatilityFit   * T.WEIGHT_VOLATILITY_FIT +
    edgeAlignment   * T.WEIGHT_EDGE_ALIGNMENT
  ) * 100) / 100;

  const tier: PlanQualityTier =
    overall >= 0.75 ? 'high'     :
    overall >= 0.55 ? 'moderate' :
    overall >= 0.35 ? 'low'      : 'insufficient';

  return {
    overall, tier,
    structureScore:  Math.round(structureScore  * 100) / 100,
    confluenceScore: Math.round(confluenceScore * 100) / 100,
    volatilityFit:   Math.round(volatilityFit   * 100) / 100,
    edgeAlignment:   Math.round(edgeAlignment   * 100) / 100,
    notes,
  };
}

// ── Step 7: Assemble TradePlan ────────────────────────────────────────────────

function _buildPlan(
  candidate: SetupCandidate,
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
): TradePlan {
  const entry     = _buildEntryZone(candidate, ctx, thesis);
  const risk      = _buildRiskLevel(candidate, entry, ctx, thesis);
  const targets   = _buildTargets(candidate, entry, risk, ctx, thesis);
  const scenarios = _buildScenarios(candidate, entry, risk, targets, ctx, thesis);
  const quality   = _scorePlan(candidate, ctx, thesis, targets);

  const summary = [
    `${candidate.direction.toUpperCase()} ${candidate.type.replace('_', ' ')}`,
    `entry ~${entry.idealEntry.toFixed(2)}`,
    `stop ${risk.stopPrice.toFixed(2)} (${risk.stopPct.toFixed(1)}%)`,
    targets[0] ? `T1: ${targets[0].price.toFixed(2)} (${targets[0].rMultiple}R)` : null,
    `quality: ${quality.tier}`,
  ].filter(Boolean).join(' · ');

  const reasoning: string[] = [
    `Setup: ${candidate.type} | Direction: ${candidate.direction} | Evidence: ${candidate.evidence}%`,
    `Entry: ${entry.type} at ${entry.idealEntry.toFixed(2)} [${entry.zoneBottom.toFixed(2)}–${entry.zoneTop.toFixed(2)}]`,
    `Risk: stop at ${risk.stopPrice.toFixed(2)} (${risk.stopPct.toFixed(1)}%) — ${risk.structuralBasis}`,
    `Targets: ${targets.map(t => `${t.price.toFixed(2)}(${t.rMultiple}R)`).join(', ') || 'none'}`,
    `Quality: ${quality.tier} (${(quality.overall * 100).toFixed(0)}%) — ${quality.notes.slice(0, 2).join('; ')}`,
    `Rules fired: ${candidate.rules.join(', ')}`,
  ];

  return {
    symbol:    ctx.symbol,
    setupType: candidate.type,
    direction: candidate.direction,
    entryZone: entry,
    risk,
    targets,
    scenarios,
    quality,
    summary,
    reasoning,
  };
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Generate execution intelligence from engine outputs and thesis.
 *
 * PURE FUNCTION — deterministic, no I/O, no Date.now() in output.
 *
 * @param ctx    — SymbolContext (post-engine, read-only)
 * @param thesis — current ValidatedAIResponse or null
 */
export function buildExecutionPlan(
  ctx:    SymbolContext,
  thesis: ValidatedAIResponse | null,
): ExecutionPlanOutput {
  // Gate: require minimum data quality
  if (!ctx.dataQuality.hasSufficientBars || ctx.curPrice <= 0) {
    return {
      symbol: ctx.symbol, candidates: [], primaryPlan: null, allPlans: [],
      noplanReason: 'Insufficient market data — need at least 30 candles',
    };
  }

  // Gate: require minimum conviction
  if (thesis && thesis.conviction < T.MIN_CONVICTION) {
    return {
      symbol: ctx.symbol, candidates: [], primaryPlan: null, allPlans: [],
      noplanReason: `Conviction too low (${thesis.conviction}% < ${T.MIN_CONVICTION}% minimum)`,
    };
  }

  const candidates = _detectSetups(ctx, thesis);

  if (candidates.length === 0) {
    return {
      symbol: ctx.symbol, candidates: [], primaryPlan: null, allPlans: [],
      noplanReason: 'No setup conditions met — awaiting structural confirmation',
    };
  }

  // Build a plan for each candidate
  const allPlans = candidates.map(c => _buildPlan(c, ctx, thesis));

  // Sort by quality overall descending
  allPlans.sort((a, b) => b.quality.overall - a.quality.overall);

  // Primary plan: highest quality, must meet actionable threshold
  const primaryPlan = allPlans[0]?.quality.overall >= T.MIN_QUALITY_ACTIONABLE
    ? allPlans[0]
    : null;

  return {
    symbol: ctx.symbol,
    candidates,
    primaryPlan,
    allPlans,
    noplanReason: primaryPlan ? null : `Best plan quality ${((allPlans[0]?.quality.overall ?? 0) * 100).toFixed(0)}% below ${(T.MIN_QUALITY_ACTIONABLE * 100)}% threshold`,
  };
}
